import { type SyntheticEvent, useLayoutEffect } from 'react';
import { useSearchParams } from 'react-router';
import { motion, type Variants } from 'framer-motion';
import type { ButtonProps } from '@archipro-design/aria';
import {
    Button,
    Flex,
    pxToRem,
    useAppDisplayModeContext,
    useStyles,
} from '@archipro-design/aria';
import {
    Download2Line,
    HeartFill,
    HeartLine,
    PriceTag3Line,
    ReseneOpenPicker,
} from '@archipro-design/icons';
import { useTracker } from '@archipro-website/tracker';
import { HoverActionArticle } from '../../common/hover-action/HoverActionArticle';
import * as S from './HoverButtonsImage.style';
import { useProductTagging } from '~/modules/product-tagging/context';
import { usePermissions } from '@modules/user';
import { PermissionEnum } from 'generated/graphql';
import { useFeatureFlaggedPaintPickerContext } from '@modules/paint-picker-new/hooks/useFeatureFlaggedPaintPickerContext';
import { HINT_ANIMATION_DURATION } from '@modules/paint-picker-new/config';
import { FollowSelector } from '~/modules/moodboards/components';
import { BOARD_TERMS } from '~/config/mood-board-terminology';
import { useHandleFollow } from '~/modules-rocco/design-board/hook/use-handle-follow';

const HOVER_BUTTON_PADDING = 12;
const HOVER_BUTTON_PADDING_MOBILE = 8;
const HOVER_BUTTON_BORDER_RADIUS = '50%';

interface HoverButtonsImageProps {
    photoID: number;
    libraryItemId?: number;
    hideReseneTool?: boolean;
    showHint?: boolean;
    clickableLink?: string;
    imageURL: string;
    downloadLink?: string;
    taggingEnabled: boolean;
}
export const encodeDownloadLink = (link: string) => {
    return link.replaceAll('+', '%2B');
};

const HoverButtonsImage = ({
    photoID,
    libraryItemId,
    hideReseneTool = true,
    showHint = true,
    imageURL,
    downloadLink,
    taggingEnabled,
}: HoverButtonsImageProps) => {
    const hasMarketingView = usePermissions(PermissionEnum.BmMarketingView);

    const {
        pickerContext: {
            state: { paintPickerActive, usageHintEnabled },
            dispatch: dispatchPaintPicker,
            openPaintPicker,
            closePaintPicker,
            setPickingProcess,
            setUsageHint,
        },
    } = useFeatureFlaggedPaintPickerContext();

    const { dispatch: dispatchProductTagging } = useProductTagging();
    const [searchParams, setSearchParams] = useSearchParams();

    const { css } = useStyles();
    const { mobile: isMobile } = useAppDisplayModeContext();
    const tracker = useTracker();

    // hide hint after some time
    useLayoutEffect(() => {
        setTimeout(() => setUsageHint(false), HINT_ANIMATION_DURATION * 1000);
    }, [setUsageHint]);

    const buttonParams: Pick<ButtonProps, 'size' | 'color' | 'variables'> = {
        size: isMobile ? 16 : 24,
        color: 'grey',
        variables: {
            buttonRadius: HOVER_BUTTON_BORDER_RADIUS,
            buttonPadding: pxToRem(
                isMobile ? HOVER_BUTTON_PADDING_MOBILE : HOVER_BUTTON_PADDING,
            ),
            iconPadding: 0,
        },
    };

    const pickerButtonParams: Pick<
        ButtonProps,
        'size' | 'color' | 'variables'
    > = {
        size: 24,
        color: 'grey',
        variables: {
            buttonRadius: HOVER_BUTTON_BORDER_RADIUS,
            buttonPadding: pxToRem(isMobile ? 4 : HOVER_BUTTON_PADDING),
            iconPadding: 0,
        },
    };

    const openPaintPalette = () => {
        dispatchPaintPicker({
            type: 'SET_ACTIVE_IMAGE',
            payload: imageURL,
        });

        openPaintPicker();
        setPickingProcess(true);
    };

    const { isInDesignBoard } = useHandleFollow();

    const scrollToImage = (
        imageElement: HTMLElement | undefined | null,
        callback: () => void,
    ) => {
        if (imageElement) {
            // Get the image's position
            const imageRect = imageElement.getBoundingClientRect();
            const paintPickerRect = document
                .getElementById('resene-paint-picker')
                ?.getBoundingClientRect();
            const scrollToHeight = paintPickerRect
                ? window.innerHeight - paintPickerRect.height
                : window.innerHeight / 2;

            // Check if the image is below the bottom half of the screen
            if (imageRect.bottom > scrollToHeight) {
                // Scroll the window to position the image above the top half
                window.scrollBy({
                    top: imageRect.bottom - scrollToHeight,
                    behavior: 'smooth',
                });

                // window.scrollBy doesn't have any event / callback / whatever
                // so use a timeout to call openPaintPalette after scrolling
                const checkScrollCompletion = () => {
                    const currentScrollPosition = window.scrollY;
                    setTimeout(() => {
                        if (window.scrollY === currentScrollPosition) {
                            callback();
                        } else {
                            checkScrollCompletion();
                        }
                    }, 100); // Check every 100ms
                };

                checkScrollCompletion();
            } else {
                callback();
            }
        }
    };

    const canDownload = !isMobile && hasMarketingView;

    const ReseneMobileButton = () => (
        <Button
            icon={<ReseneOpenPicker />}
            onTouchStart={(e: SyntheticEvent) => {
                if (!paintPickerActive) {
                    dispatchPaintPicker({
                        type: 'SET_TOUCH_ENABLED',
                        payload: true,
                    });

                    // Find the image element two levels above the button
                    const buttonElement = e.currentTarget;
                    const imageElement =
                        buttonElement.parentElement?.parentElement
                            ?.parentElement;
                    scrollToImage(imageElement, openPaintPalette);
                } else {
                    closePaintPicker();
                }
            }}
            {...pickerButtonParams}
        />
    );

    return (
        <Flex
            className={`${css(
                S.HoverButtonsImageContainer,
            )} hover-buttons-image`}
            vAlign="start"
        >
            {isMobile && !hideReseneTool && (
                <HoverActionArticle
                    label="Show Resene paint ideas"
                    action={
                        showHint ? (
                            <motion.div
                                initial={false}
                                className={css(S.HoverButtonsHintContainer)}
                                animate={
                                    usageHintEnabled ? 'hintOn' : 'hintOff'
                                }
                                variants={S.hintAnimation}
                            >
                                <motion.div
                                    className={css(S.HoverButtonsHintLabel)}
                                    variants={S.hintLabelAnimation as Variants}
                                >
                                    Tap to use Colour selector
                                </motion.div>
                                <ReseneMobileButton />
                            </motion.div>
                        ) : (
                            <ReseneMobileButton />
                        )
                    }
                />
            )}
            {taggingEnabled && (
                <HoverActionArticle
                    label="Start tagging"
                    action={
                        <Button
                            icon={<PriceTag3Line />}
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setSearchParams(
                                    {
                                        ...searchParams,
                                        tagging: 'true',
                                    },
                                    {
                                        replace: false,
                                        preventScrollReset: true,
                                    },
                                );

                                dispatchProductTagging({
                                    type: 'TOGGLE_TAGGING',
                                });
                            }}
                            {...buttonParams}
                        />
                    }
                />
            )}
            <HoverActionArticle
                label={BOARD_TERMS.saveTo}
                action={
                    <FollowSelector
                        trigger={
                            <Button
                                icon={
                                    isInDesignBoard(photoID, 'PhotoPin') ? (
                                        <HeartFill />
                                    ) : (
                                        <HeartLine />
                                    )
                                }
                                {...buttonParams}
                            />
                        }
                        pinId={photoID}
                        libraryItemId={libraryItemId}
                        pinType="PhotoPin"
                    />
                }
            />
            {canDownload && downloadLink && (
                <HoverActionArticle
                    label={'Download'}
                    action={
                        <Button
                            as="a"
                            icon={<Download2Line />}
                            href={encodeDownloadLink(downloadLink)}
                            download
                            {...buttonParams}
                            onClick={(e) => {
                                e.stopPropagation();
                                tracker.log('StaffDownloadImage', {
                                    url: new URL(window.location.href),
                                    targetTracker: 'archiproTracker',
                                    data: {
                                        // ImageID
                                        ExtraData: photoID,
                                    },
                                });
                            }}
                        />
                    }
                />
            )}
        </Flex>
    );
};

export default HoverButtonsImage;
